/**
 * Regole di validazione del form NAC. Replicano quelle del backend
 * (cedi_backend/src/nac/nac.validators.ts): il backend resta l'autorita'.
 */

export interface NacReferente {
  nome_cognome: string;
  ruolo: string;
  telefono: string;
  email: string;
  principale: boolean;
}

export interface NacIndirizzo {
  nome_destinazione: string;
  destinatario: string;
  indirizzo: string;
  numero_civico: string;
  cap: string;
  comune: string;
  provincia: string;
  nazione: string;
  referente_consegna: string;
  telefono_consegna: string;
  note_logistiche: string;
  orari_ricevimento: string;
  predefinito: boolean;
}

export type NacSoggetto = "Azienda" | "Privato";

export interface NacFormValues {
  tipologia_soggetto: NacSoggetto;
  /** Ragione sociale (Azienda) oppure Nome e cognome (Privato). */
  ragione_sociale: string;
  partita_iva: string;
  codice_fiscale: string;
  indirizzo: string;
  numero_civico: string;
  cap: string;
  comune: string;
  provincia: string;
  nazione: string;
  pec: string;
  codice_sdi: string;
  data_inizio_attivita: string;
  telefono: string;
  cellulare: string;
  email: string;
  sito_web: string;
  referenti: NacReferente[];
  indirizzi_spedizione: NacIndirizzo[];
  condizioni_pagamento: string;
  banca_appoggio: string;
  iban: string;
  email_documenti: string;
  note_amministrative: string;
  categoria_vendita: string[];
  tipologia_cliente: number[];
  marca_cucina: number[];
  marca_elettrodomestici: number[];
  distributore: number[];
  numero_cucine_esposte: string;
  numero_venditori: string;
  stima_cucine_anno: string;
  valutazione_commerciale: string;
  valutazione_amministrativa: string;
  potenziale_acquisto: string;
  commento: string;
  /** Scelto dal cliente nella pagina di conferma: solo in lettura per l'agente. */
  consenso_invio_telematico: "" | "si" | "no";
}

export const RUOLI = ["Titolare", "Amministrazione", "Acquisti", "Magazzino", "Altro"];

export const emptyReferente = (principale = false): NacReferente => ({
  nome_cognome: "", ruolo: "Titolare", telefono: "", email: "", principale,
});

export const emptyIndirizzo = (predefinito = false): NacIndirizzo => ({
  nome_destinazione: "", destinatario: "", indirizzo: "", numero_civico: "", cap: "", comune: "",
  provincia: "", nazione: "Italia", referente_consegna: "", telefono_consegna: "",
  note_logistiche: "", orari_ricevimento: "", predefinito,
});

export const isPrivato = (v: Pick<NacFormValues, "tipologia_soggetto">) => v.tipologia_soggetto === "Privato";

/** Campi riservati alle aziende: per un Privato sono nascosti e non vengono salvati. */
export function clearCompanyOnly(v: NacFormValues): NacFormValues {
  return {
    ...v,
    partita_iva: "",
    data_inizio_attivita: "",
    sito_web: "",
    referenti: [],
    numero_cucine_esposte: "",
    numero_venditori: "",
    stima_cucine_anno: "",
    marca_cucina: [],
    marca_elettrodomestici: [],
    distributore: [],
  };
}

/** Ci sono dati nei campi che un cambio a Privato cancellerebbe? */
export function hasCompanyOnlyData(v: NacFormValues): boolean {
  return (
    !blank(v.partita_iva) || !blank(v.data_inizio_attivita) || !blank(v.sito_web) ||
    v.referenti.some((r) => !blank(r.nome_cognome) || !blank(r.telefono) || !blank(r.email)) ||
    !blank(v.numero_cucine_esposte) || !blank(v.numero_venditori) || !blank(v.stima_cucine_anno) ||
    v.marca_cucina.length > 0 || v.marca_elettrodomestici.length > 0 || v.distributore.length > 0
  );
}

export const emptyNacForm = (): NacFormValues => ({
  tipologia_soggetto: "Azienda",
  ragione_sociale: "", partita_iva: "", codice_fiscale: "", indirizzo: "", numero_civico: "", cap: "",
  comune: "", provincia: "", nazione: "Italia", pec: "", codice_sdi: "", data_inizio_attivita: "",
  telefono: "", cellulare: "", email: "", sito_web: "",
  referenti: [emptyReferente(true)],
  indirizzi_spedizione: [emptyIndirizzo(true)],
  condizioni_pagamento: "", banca_appoggio: "", iban: "", email_documenti: "", note_amministrative: "",
  categoria_vendita: [], tipologia_cliente: [], marca_cucina: [], marca_elettrodomestici: [], distributore: [],
  numero_cucine_esposte: "", numero_venditori: "", stima_cucine_anno: "",
  valutazione_commerciale: "", valutazione_amministrativa: "", potenziale_acquisto: "", commento: "",
  consenso_invio_telematico: "",
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const isEmail = (v: string) => EMAIL_RE.test(v.trim());

export function isPartitaIva(v: string): boolean {
  if (!/^\d{11}$/.test(v)) return false;
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    let d = Number(v[i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

export const isCodiceFiscale = (v: string) =>
  /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(v.toUpperCase()) || /^\d{11}$/.test(v);

export function isIban(v: string): boolean {
  const iban = v.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const numeric = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  let rem = 0;
  for (const ch of numeric) rem = (rem * 10 + Number(ch)) % 97;
  return rem === 1;
}

const blank = (v: unknown) => v === undefined || v === null || String(v).trim() === "";

/** Recapito aziendale: almeno uno tra telefono, cellulare ed e-mail. True finche' sono tutti vuoti (mostra l'asterisco sui tre campi). */
export const contactRequired = (v: Pick<NacFormValues, "telefono" | "cellulare" | "email">) =>
  blank(v.telefono) && blank(v.cellulare) && blank(v.email);

/** Referente: almeno un telefono o un'e-mail. True finche' sono entrambi vuoti. */
export const referenteContactRequired = (r: Pick<NacReferente, "telefono" | "email">) =>
  blank(r.telefono) && blank(r.email);
const isItalia = (n: string) => blank(n) || n.trim().toLowerCase() === "italia";
const intIn = (v: string, min: number, max = Infinity) => /^\d+$/.test(v) && Number(v) >= min && Number(v) <= max;

export type NacErrors = {
  fields: Record<string, string>;
  referenti: Record<number, Record<string, string>>;
  indirizzi: Record<number, Record<string, string>>;
};

export function validateNacForm(v: NacFormValues): NacErrors {
  const f: Record<string, string> = {};
  const req = (k: keyof NacFormValues, label: string) => {
    if (blank(v[k])) f[k] = `${label} obbligatorio`;
  };
  const privato = isPrivato(v);
  req("ragione_sociale", privato ? "Nome e cognome" : "Ragione sociale");
  if (!privato) req("partita_iva", "Partita IVA");
  // Per un'Azienda il codice fiscale e' facoltativo (se presente viene comunque validato)
  if (privato) req("codice_fiscale", "Codice fiscale");
  req("indirizzo", privato ? "Residenza" : "Indirizzo");
  req("numero_civico", "Numero civico");
  req("cap", "CAP");
  req("comune", "Comune");
  req("provincia", "Provincia");
  req("nazione", "Nazione");
  req("condizioni_pagamento", "Condizioni di pagamento");
  req("banca_appoggio", "Banca d'appoggio");
  req("iban", "IBAN");
  req("email_documenti", "E-mail invio documenti");
  req("commento", "Commento");

  const piva = v.partita_iva.replace(/\s+/g, "");
  if (!privato && !blank(piva) && !isPartitaIva(piva)) f.partita_iva = "Partita IVA non valida";
  if (!blank(v.codice_fiscale)) {
    const cf = v.codice_fiscale.replace(/\s+/g, "");
    const ok = privato ? /^[A-Za-z0-9]{16}$/.test(cf) : isCodiceFiscale(cf);
    if (!ok) f.codice_fiscale = "Codice fiscale non valido";
  }
  if (!blank(v.iban) && !isIban(v.iban)) f.iban = "IBAN non valido";
  for (const k of ["pec", "email", "email_documenti"] as const) {
    if (!blank(v[k]) && !isEmail(v[k])) f[k] = "E-mail non valida";
  }
  if (isItalia(v.nazione)) {
    if (!blank(v.cap) && !/^\d{5}$/.test(v.cap)) f.cap = "CAP non valido (5 cifre)";
    if (!blank(v.provincia) && !/^[A-Za-z]{2}$/.test(v.provincia)) f.provincia = "Provincia: 2 lettere";
  }
  if (!blank(v.codice_sdi) && !/^[A-Za-z0-9]{6,7}$/.test(v.codice_sdi)) f.codice_sdi = "Codice SDI non valido";
  if (contactRequired(v)) {
    f.telefono = "Inserire almeno un recapito (telefono, cellulare o e-mail)";
  }

  const referentiDaControllare = privato ? [] : v.referenti;
  if (!privato && v.referenti.length === 0) f.referenti = "Inserire almeno un referente";
  else if (!privato && v.referenti.filter((r) => r.principale).length !== 1) f.referenti = "Indicare un solo referente principale";
  const referenti: NacErrors["referenti"] = {};
  referentiDaControllare.forEach((r, i) => {
    const e: Record<string, string> = {};
    if (blank(r.nome_cognome)) e.nome_cognome = "Obbligatorio";
    if (referenteContactRequired(r)) e.telefono = "Telefono o e-mail";
    if (!blank(r.email) && !isEmail(r.email)) e.email = "E-mail non valida";
    if (Object.keys(e).length) referenti[i] = e;
  });

  if (v.indirizzi_spedizione.length === 0) f.indirizzi_spedizione = "Inserire almeno un indirizzo di spedizione";
  else if (v.indirizzi_spedizione.filter((a) => a.predefinito).length !== 1) f.indirizzi_spedizione = "Indicare un solo indirizzo predefinito";
  const indirizzi: NacErrors["indirizzi"] = {};
  v.indirizzi_spedizione.forEach((a, i) => {
    const e: Record<string, string> = {};
    for (const k of ["nome_destinazione", "destinatario", "indirizzo", "cap", "comune", "provincia", "nazione", "referente_consegna", "telefono_consegna"] as const) {
      if (blank(a[k])) e[k] = "Obbligatorio";
    }
    if (!e.cap && isItalia(a.nazione) && !/^\d{5}$/.test(a.cap)) e.cap = "CAP non valido";
    if (Object.keys(e).length) indirizzi[i] = e;
  });

  if (v.categoria_vendita.length === 0) f.categoria_vendita = "Categoria vendita obbligatoria";
  if (v.tipologia_cliente.length === 0) f.tipologia_cliente = "Tipologia cliente obbligatoria";
  for (const k of ["marca_cucina", "marca_elettrodomestici", "distributore"] as const) {
    if (v[k].length > 3) f[k] = "Massimo 3 selezioni";
  }
  if (!privato) {
    for (const k of ["numero_cucine_esposte", "numero_venditori", "stima_cucine_anno"] as const) {
      if (blank(v[k])) f[k] = "Obbligatorio";
      else if (!intIn(v[k], 0)) f[k] = "Numero intero non negativo";
    }
  }
  for (const k of ["valutazione_commerciale", "valutazione_amministrativa"] as const) {
    if (blank(v[k])) f[k] = "Obbligatoria";
    else if (!intIn(v[k], 1, 10)) f[k] = "Valore da 1 a 10";
  }
  if (!blank(v.potenziale_acquisto) && !(Number(v.potenziale_acquisto) >= 0)) f.potenziale_acquisto = "Importo non valido";

  return { fields: f, referenti, indirizzi };
}

export const countErrors = (e: NacErrors) =>
  Object.keys(e.fields).length +
  Object.values(e.referenti).reduce((n, x) => n + Object.keys(x).length, 0) +
  Object.values(e.indirizzi).reduce((n, x) => n + Object.keys(x).length, 0);

/** Valori del form -> corpo della richiesta al backend. */
export function formToPayload(input: NacFormValues) {
  const v = isPrivato(input) ? clearCompanyOnly(input) : input;
  const num = (s: string) => (blank(s) ? null : Number(s));
  const selections = [
    ...v.categoria_vendita.map((value) => ({ kind: "categoria_vendita", value })),
    ...v.tipologia_cliente.map((lookup_id) => ({ kind: "tipologia_cliente", lookup_id })),
    ...v.marca_cucina.map((lookup_id) => ({ kind: "marca_cucina", lookup_id })),
    ...v.marca_elettrodomestici.map((lookup_id) => ({ kind: "marca_elettrodomestici", lookup_id })),
    ...v.distributore.map((lookup_id) => ({ kind: "distributore", lookup_id })),
  ];
  const { categoria_vendita, tipologia_cliente, marca_cucina, marca_elettrodomestici, distributore, ...rest } = v;
  return {
    ...rest,
    data_inizio_attivita: blank(v.data_inizio_attivita) ? null : v.data_inizio_attivita,
    numero_cucine_esposte: num(v.numero_cucine_esposte),
    numero_venditori: num(v.numero_venditori),
    stima_cucine_anno: num(v.stima_cucine_anno),
    valutazione_commerciale: num(v.valutazione_commerciale),
    valutazione_amministrativa: num(v.valutazione_amministrativa),
    potenziale_acquisto: num(v.potenziale_acquisto),
    selections,
  };
}

/** Risposta del backend (GET /nac/:id) -> valori del form. */
export function detailToForm(d: any): NacFormValues {
  const r = d.request ?? {};
  const s = (x: unknown) => (x === null || x === undefined ? "" : String(x));
  const picks = (kind: string) => (d.selections ?? []).filter((x: any) => x.kind === kind);
  return {
    ...emptyNacForm(),
    tipologia_soggetto: r.tipologia_soggetto === "Privato" ? "Privato" : "Azienda",
    ragione_sociale: s(r.ragione_sociale), partita_iva: s(r.partita_iva), codice_fiscale: s(r.codice_fiscale),
    indirizzo: s(r.indirizzo), numero_civico: s(r.numero_civico), cap: s(r.cap), comune: s(r.comune),
    provincia: s(r.provincia), nazione: s(r.nazione) || "Italia", pec: s(r.pec), codice_sdi: s(r.codice_sdi),
    data_inizio_attivita: s(r.data_inizio_attivita).slice(0, 10),
    telefono: s(r.telefono), cellulare: s(r.cellulare), email: s(r.email), sito_web: s(r.sito_web),
    referenti: (d.referenti?.length ? d.referenti : [emptyReferente(true)]).map((x: any) => ({
      nome_cognome: s(x.nome_cognome), ruolo: s(x.ruolo) || "Titolare", telefono: s(x.telefono), email: s(x.email), principale: !!x.principale,
    })),
    indirizzi_spedizione: (d.indirizzi_spedizione?.length ? d.indirizzi_spedizione : [emptyIndirizzo(true)]).map((a: any) => ({
      nome_destinazione: s(a.nome_destinazione), destinatario: s(a.destinatario), indirizzo: s(a.indirizzo),
      numero_civico: s(a.numero_civico), cap: s(a.cap), comune: s(a.comune), provincia: s(a.provincia),
      nazione: s(a.nazione) || "Italia", referente_consegna: s(a.referente_consegna), telefono_consegna: s(a.telefono_consegna),
      note_logistiche: s(a.note_logistiche), orari_ricevimento: s(a.orari_ricevimento), predefinito: !!a.predefinito,
    })),
    condizioni_pagamento: s(r.condizioni_pagamento), banca_appoggio: s(r.banca_appoggio), iban: s(r.iban),
    email_documenti: s(r.email_documenti), note_amministrative: s(r.note_amministrative),
    categoria_vendita: picks("categoria_vendita").map((x: any) => x.value),
    tipologia_cliente: picks("tipologia_cliente").map((x: any) => x.lookup_id),
    marca_cucina: picks("marca_cucina").map((x: any) => x.lookup_id),
    marca_elettrodomestici: picks("marca_elettrodomestici").map((x: any) => x.lookup_id),
    distributore: picks("distributore").map((x: any) => x.lookup_id),
    numero_cucine_esposte: s(r.numero_cucine_esposte), numero_venditori: s(r.numero_venditori),
    stima_cucine_anno: s(r.stima_cucine_anno), valutazione_commerciale: s(r.valutazione_commerciale),
    valutazione_amministrativa: s(r.valutazione_amministrativa), potenziale_acquisto: s(r.potenziale_acquisto),
    commento: s(r.commento),
    consenso_invio_telematico: r.consenso_invio_telematico === true ? "si" : r.consenso_invio_telematico === false ? "no" : "",
  };
}
